import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Button,
  Avatar,
  Card,
  CardContent,
} from "@mui/material";
import StoreIcon from "@mui/icons-material/Store";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import StorageIcon from "@mui/icons-material/Storage";
import {
  MdGpsFixed,
  MdLocationOn,
  MdBusiness,
  MdInventory2,
  MdLocalOffer,
  MdEvent,
  MdRequestQuote,
  MdPeople,
} from "react-icons/md";
import { FaUsers } from "react-icons/fa";
import { API_BASE_URL, getAuthHeaders } from "../API/Api";

const REFRESH_INTERVAL = 60000;

const CARD_SHADOW = "0px 2px 8px rgba(0, 0, 0, 0.1)";

const PAGES = [
  { title: "GPS Device", to: "/GetGPS", icon: <MdGpsFixed />, color: "#3f51b5" },
  { title: "Routers", to: "/PostGetRouter", icon: <FaUsers />, color: "#2c3e50" },
  { title: "Location", to: "/Maps", icon: <MdLocationOn />, color: "#3A6F43" },
  { title: "Brands", to: "/admin/brands", icon: <MdBusiness />, color: "#3A6F43" },
  {
    title: "Products",
    to: "/admin/products",
    icon: <MdInventory2 />,
    color: "#3f51b5",
  },
  {
    title: "Promotions",
    to: "/admin/promotions",
    icon: <MdLocalOffer />,
    color: "#e67e22",
  },
  {
    title: "Quotes",
    to: "/admin/quotes",
    icon: <MdRequestQuote />,
    color: "#2c3e50",
  },
  { title: "Users", to: "/admin/users", icon: <MdPeople />, color: "#0984e3" },
  { title: "Events", to: "/admin/events", icon: <MdEvent />, color: "#8e44ad" },
];

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [analytics, setAnalytics] = useState({
    products_count: 0,
    brands_count: 0,
    storage_usage: 0,
  });

  const formatStorage = useCallback((bytes) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${sizes[i]}`;
  }, []);

  const stats = useMemo(
    () => [
      {
        title: "Products",
        value: analytics.products_count,
        icon: <ShoppingBasketIcon />,
        color: "#3f51b5",
        to: "/admin/products",
      },
      {
        title: "Brands",
        value: analytics.brands_count,
        icon: <StoreIcon />,
        color: "#3A6F43",
        to: "/admin/brands",
      },
      {
        title: "Storage",
        value: formatStorage(analytics.storage_usage),
        icon: <StorageIcon />,
        color: "#2c3e50",
      },
    ],
    [analytics, formatStorage],
  );

  const fetchAnalytics = useCallback(async () => {
    try {
      setError("");
      const res = await fetch(`${API_BASE_URL}/admin/analytics`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      const responseData = await res.json();
      const data = responseData?.data ?? responseData;
      setAnalytics({
        products_count: Number(data?.products_count) || 0,
        brands_count: Number(data?.brands_count) || 0,
        storage_usage: Number(data?.storage_usage) || 0,
      });
    } catch (err) {
      setError("We couldn't load the dashboard right now.");
      console.error("Error fetching analytics:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
    const intervalId = setInterval(fetchAnalytics, REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [fetchAnalytics]);

  const handleRetry = () => {
    setLoading(true);
    fetchAnalytics();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: 300,
          m: "25px",
        }}
      >
        <CircularProgress size={60} sx={{ color: "#3f51b5" }} />
        <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
          Loading Dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          m: "25px",
          p: "24px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: CARD_SHADOW,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
          {error}
        </Typography>
        <Button
          variant="outlined"
          onClick={handleRetry}
          sx={{
            mt: 1,
            borderColor: "#3f51b5",
            color: "#3f51b5",
            "&:hover": { borderColor: "#303f9f", backgroundColor: "#eef0fa" },
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ m: "25px", pb: 10, overflow: "hidden" }}>
      <Box
        sx={{
          padding: { xs: "16px", sm: "16px 24px" },
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: CARD_SHADOW,
          mb: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            color: "#2c3e50",
            fontSize: { xs: "1.4rem", sm: "1.6rem" },
          }}
        >
          Dashboard
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((item) => {
          const card = (
            <Card
              sx={{
                height: "100%",
                backgroundColor: "#fff",
                borderRadius: "8px",
                boxShadow: CARD_SHADOW,
                position: "relative",
                overflow: "hidden",
                transition: "transform 0.2s, box-shadow 0.2s",
                cursor: item.to ? "pointer" : "default",
                "&:hover": item.to
                  ? {
                      transform: "translateY(-3px)",
                      boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.12)",
                    }
                  : undefined,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 6,
                  backgroundColor: item.color,
                },
              }}
            >
              <CardContent sx={{ pl: 3, pr: 2, py: 2.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 0.75,
                        color: "#6b7280",
                        fontWeight: 600,
                        letterSpacing: 0.3,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        color: "#1f2937",
                        fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                      }}
                    >
                      {typeof item.value === "number"
                        ? item.value.toLocaleString()
                        : item.value}
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: `${item.color}1A`,
                      color: item.color,
                      width: 48,
                      height: 48,
                    }}
                  >
                    {item.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          );

          return (
            <Grid item xs={12} sm={6} md={4} key={item.title}>
              {item.to ? (
                <Link to={item.to} style={{ textDecoration: "none" }}>
                  {card}
                </Link>
              ) : (
                card
              )}
            </Grid>
          );
        })}
      </Grid>

      <Typography
        variant="h6"
        sx={{ fontWeight: 800, color: "#2c3e50", mb: 2 }}
      >
        Pages
      </Typography>

      <Grid container spacing={2}>
        {PAGES.map((page) => (
          <Grid item xs={12} sm={6} md={4} key={page.to}>
            <Link to={page.to} style={{ textDecoration: "none" }}>
              <Card
                sx={{
                  height: "100%",
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  boxShadow: CARD_SHADOW,
                  position: "relative",
                  overflow: "hidden",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.12)",
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 6,
                    backgroundColor: page.color,
                  },
                }}
              >
                <CardContent sx={{ pl: 3, pr: 2, py: 2.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: "#1f2937",
                        fontSize: { xs: "1.05rem", sm: "1.15rem" },
                      }}
                    >
                      {page.title}
                    </Typography>
                    <Avatar
                      sx={{
                        bgcolor: `${page.color}1A`,
                        color: page.color,
                        width: 44,
                        height: 44,
                        fontSize: 22,
                      }}
                    >
                      {page.icon}
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Link>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Dashboard;
